import { createFileRoute, useLoaderData } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback } from 'react';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { AlertPage } from '../-shared/AlertPage';
import { createSearchSchema } from '../-shared/search-schema';
import type { AlertRowWithUserAndWorker, AlertSearchParams } from '../-shared/types';
import { createAlertExportHook, createAlertQueryHook } from '../-shared/useAlertQuery';
import { SoftwareAlertType } from '../../-zod-schema';
import { ColumnKey, SortableColumnKey, useSoftwareAlertsViolatedRoleColumns } from './-hooks/useColumns';
const SearchSchema = createSearchSchema(SortableColumnKey);
const useAlertQuery = createAlertQueryHook<AlertRowWithUserAndWorker>({
  category: 'VIOLATED_ROLE',
  queryKeySegment: 'violated-role',
  sortColumnMap: {
    [ColumnKey.appUser]: 'USER',
    [ColumnKey.worker]: 'PERSON',
    [ColumnKey.app]: 'APP',
    worker_assignedRoleName: 'APP_ASSIGNED_ROLE',
    user_actualRoleName: 'APP_USER_ROLE',
    user_statusName: 'APP_USER_STATUS',
    user_lastSignedInAt: 'LAST_SIGNED_IN_AT',
    user_createdAt: 'CREATED_AT'
  },
  filterAndMap: (items): AlertRowWithUserAndWorker[] => items.filter(item => item.user && item.worker).map(item => ({
    ...item,
    user: item.user as NonNullable<typeof item.user>,
    worker: item.worker as NonNullable<typeof item.worker>
  }))
});
const useExport = createAlertExportHook('VIOLATED_ROLE');
export const Route = createFileRoute('/_staff/software/_layout/alerts/_alerts/violated-role/')({
  validateSearch: zodValidator(SearchSchema),
  component: SoftwareAlertsViolatedRole
});
function SoftwareAlertsViolatedRole() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const counts = useLoaderData({
    from: Route.parentRoute.id
  });
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.software.alert'
  });
  const {
    data,
    isPending
  } = useAlertQuery(search);
  const {
    mutate: onExport,
    isPending: exporting
  } = useExport(search);
  const columns = useSoftwareAlertsViolatedRoleColumns();
  const onSearch = useCallback((query: Partial<AlertSearchParams>) => {
    navigate({
      search: prev => ({
        ...prev,
        ...query
      }) as typeof prev
    });
  }, [navigate]);
  return <AlertPage alertTypeKey={SoftwareAlertType.violatedRole.key} helpTitle={t('help.violatedAccountRole.title')} helpText={t('help.violatedAccountRole.text')} counts={counts} search={search} onSearch={onSearch} data={data} isPending={isPending} exporting={exporting} onExport={onExport} columns={columns} columnPinningRight={['actions']} />;
}